import descopeSdk from '@descope/node-sdk';
import type { AuthenticationInfo } from '@descope/node-sdk';
import type { NextRequest } from 'next/server';
import { createSdk } from '@descope/nextjs-sdk/server';
import { getConfigValue } from '../../lib/public-env/get-config-value';
import type { AuthMiddlewareOptions } from './types';
import { pathToRegexp } from 'path-to-regexp';

export const runtime = 'nodejs';

type Sdk = ReturnType<typeof createSdk>;
type CreateSdkParams = Omit<Parameters<typeof descopeSdk>[0], 'projectId'> & {
  projectId?: string | undefined;
};

// Cache SDKs per project ID for terra-proxy's multi-environment support
const sdkCache: Map<string, Sdk> = new Map();

export const DESCOPE_SESSION_HEADER = 'x-descope-session';

export const baseHeaders = {
  'x-descope-sdk-name': 'nextjs',
};

export const getGlobalSdk = (
  config?: Pick<CreateSdkParams, 'projectId' | 'baseUrl'>,
): Sdk => {
  const projectId =
    config?.projectId || getConfigValue('NEXT_PUBLIC_DESCOPE_PROJECT_ID');

  if (!projectId) {
    throw new Error('No Descope project ID provided');
  }

  // Use cached SDK if available
  const cachedSdk = sdkCache.get(projectId);
  if (cachedSdk) {
    return cachedSdk;
  }

  // Create new SDK and cache it
  const sdk = createSdk(config);
  sdkCache.set(projectId, sdk);
  return sdk;
};

export const getSessionJwt = (req: NextRequest): string => {
  const authHeader = req.headers.get('Authorization');
  if (authHeader?.startsWith('Bearer ')) {
    return authHeader.slice(7);
  }

  const sessionCookieName =
    getConfigValue('NEXT_PUBLIC_DESCOPE_SESSION_COOKIE_NAME') ??
    descopeSdk.SessionTokenCookieName;

  return req.cookies.get(sessionCookieName)?.value ?? '';
};

export const addSessionToHeadersIfExists = (
  headers: Headers,
  session: AuthenticationInfo | undefined,
): Headers => {
  if (session) {
    const requestHeaders = new Headers(headers);
    requestHeaders.set(
      DESCOPE_SESSION_HEADER,
      Buffer.from(JSON.stringify(session)).toString('base64'),
    );
    return requestHeaders;
  }
  return headers;
};

export const isPublicUrl = (
  req: NextRequest,
  publicRoutes: AuthMiddlewareOptions['publicRoutes'],
): boolean => {
  const routePatterns = [publicRoutes || ''].flat().filter(Boolean);
  const matchers = routePatterns.map((pattern) =>
    pattern instanceof RegExp
      ? pattern
      : pathToRegexp(pattern, { end: false }).regexp,
  );
  return matchers.some((matcher) => matcher.test(req.nextUrl.pathname));
};
